import { z } from 'zod';

import { NominationSchema } from '../nomination/nomination';

/**
 * A member's nominations for one day (P16b).
 *
 * ## The date is on the batch, not on every row
 *
 * Every nomination in the response shares it — repeating it per row would be
 * both noise and an invitation to a client that renders a mixed list. The batch
 * is the unit the whole feature is idempotent over: the same member asking on
 * the same day gets the same set, however many times and however concurrently
 * they ask.
 *
 * **It is the deployment's calendar day**, resolved through `DateTimeService` in
 * the active timezone (N3) — a plain `YYYY-MM-DD`, never an instant. That has
 * one consequence worth stating out loud rather than discovering: a member who
 * crosses midnight while travelling gets **one** batch, not two. The day belongs
 * to the deployment, not to the device, which is also the only reading under
 * which "the same set for the same date" survives a member changing timezone.
 *
 * ## An empty batch is an answer for now
 *
 * `nominations: []` means the scorer ran and found nobody this member may be
 * shown — every candidate excluded, or none in the population. Since F14 it is
 * an answer for **now**, not a claim on the day: the api asks again on the next
 * request, so a candidate who becomes eligible at noon is shown at noon. A
 * non-empty batch is stored and stays the same for the day, for as long as the
 * browse settings it was computed under hold.
 */
export const NominationBatchSchema = z
  .object({
    /** The deployment's calendar day, `YYYY-MM-DD`. */
    nominatedForDate: z.iso.date(),
    nominations: z.array(NominationSchema),
  })
  .strict()
  .refine((batch) => isStrictlyRanked(batch.nominations), {
    message: 'Nominations are ordered by rank, and no two share one.',
    path: ['nominations'],
  });

export type NominationBatch = z.infer<typeof NominationBatchSchema>;

/**
 * Ranks ascend, strictly, in the order the array is in.
 *
 * **Ascending, deliberately not contiguous.** `1, 2, 4` is a legitimate batch: a
 * nominated account that is deleted takes its row with it (the FK cascades), and
 * a contiguity check would turn that ordinary event into a 500 on a member's own
 * list. What must never happen is two rows claiming one place, or a list whose
 * array order contradicts the ranks it carries — both of which would make the
 * client's rendering disagree with the server's ordering.
 */
function isStrictlyRanked(nominations: readonly { rank: number }[]): boolean {
  return nominations.every(
    (nomination, index) => index === 0 || nomination.rank > (nominations[index - 1]?.rank ?? 0),
  );
}
